import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { PapiDeviation } from '@wix/da-papi-types';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import { pushSubmitModal } from '@wix/da-submit-shared/pkg/redux/actions';
import { editOriginalDeviation } from '@wix/da-submit-shared/pkg/redux/actions/originals';
import { editDeviation } from '@wix/da-shared-react/pkg/redux/deviations/actionCreators';
import { openCommissionEditModal } from '@wix/da-shared-react/pkg/redux/commissions/actionCreators';
import { getDeviationType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { DeviationTypes } from '@wix/da-shared-react/pkg/types/deviation';
import {
  getActiveSection,
  getIsDraftsSection,
  getIsPublishedSection,
} from '../selectors/studio';
import { StudioSection } from '../types/store';

export function useEditDeviationClick(targetDeviation?: PapiDeviation) {
  const dispatchEditDeviation = useAction(editDeviation);
  const dispatchEditCommission = useAction(openCommissionEditModal);
  const dispatchPushSubmitModal = useAction(pushSubmitModal);
  const dispatchEditOriginalDeviation = useAction(editOriginalDeviation);
  const isDraftsSection = useSelector(getIsDraftsSection);
  const isPublishedSection = useSelector(getIsPublishedSection);
  const activeSection = useSelector(getActiveSection);

  const isOriginalsSection =
    activeSection === StudioSection.PRODUCTS_ORIGINAL ||
    activeSection === StudioSection.PRODUCTS_ORIGINAL_UNLISTED;

  const openEditModal = useCallback(
    (deviation: PapiDeviation) => {
      // For originals section, fetch init data and open the original edit modal
      if (isOriginalsSection) {
        dispatchEditOriginalDeviation({ deviationId: deviation.deviationId });
        return;
      }

      const deviationType = getDeviationType(deviation);
      // We'd like to show the literature editor before the submit editor if this is a literature deviation
      if (
        [
          DeviationTypes.LITERATURE,
          DeviationTypes.JOURNAL,
          DeviationTypes.TIER,
          DeviationTypes.STATUS,
        ].includes(deviationType)
      ) {
        dispatchEditDeviation({ deviation });
      } else if (deviationType === DeviationTypes.COMMISSION) {
        dispatchEditCommission({
          deviation,
          preload: true,
          redirectOnSuccess: false,
        });
      } else {
        dispatchPushSubmitModal({
          deviationId: deviation.deviationId,
          isDraft: isDraftsSection,
          isEdit: isPublishedSection,
        });
      }
    },
    [
      dispatchPushSubmitModal,
      dispatchEditOriginalDeviation,
      dispatchEditCommission,
      dispatchEditDeviation,
      isDraftsSection,
      isPublishedSection,
      isOriginalsSection,
    ]
  );

  const onEditClick = useCallback(() => {
    if (targetDeviation) {
      openEditModal(targetDeviation);
    }
  }, [openEditModal, targetDeviation]);

  return { openEditModal, onEditClick };
}
